import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
} from "@heroicons/react/24/solid";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import Loader from "../customComponents/customLoader";
import { serviceUrl, genericHeaders } from "../../utils/apiConfig";
import { get_file_from_s3 } from "../../utils";
import NoContent from "../../Icons/No-Content-Laptop.svg";

/* ── Detail modal ──────────────────────────────────────────── */
const StatusReportPendingDetailModal = ({ sheet, onDownload }) => {
  if (!sheet) return null;

  const billingPeriod =
    sheet.billingPeriodMonth && sheet.billingPeriodYear
      ? dayjs()
          .month(sheet.billingPeriodMonth - 1)
          .year(sheet.billingPeriodYear)
          .format("MMMM YYYY")
      : "—";

  const submittedAt = sheet.submittedAt
    ? dayjs(sheet.submittedAt + "Z").format("DD MMM YYYY, hh:mm A")
    : "—";

  return (
    <div className="flex space-x-8">
      {/* ── Left: details ───────────────────────────────── */}
      <div className="w-3/4">
        <h2 className="text-black font-semibold mx-2 my-2 text-lg">
          Status Report — {billingPeriod}
        </h2>

        <div className="flex flex-col border border-gray-300 rounded-2xl bg-white">
          {/* Status row */}
          <div className="flex justify-between items-center px-6 pt-4 pb-2">
            <div className="flex flex-col">
              <p className="text-sm text-gray-500">Submitted by</p>
              <p className="text-base font-bold">
                {sheet.submittedByName ?? "—"}
              </p>
            </div>
            <span className="text-xl font-semibold text-blue-500">
              {sheet.status}
            </span>
          </div>

          {/* Highlight strip */}
          <div className="mx-5 mb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-5 py-3 flex items-center justify-around">
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Month</p>
                <p className="font-bold">
                  {sheet.billingPeriodMonth
                    ? dayjs().month(sheet.billingPeriodMonth - 1).format("MMMM")
                    : "—"}
                </p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Year</p>
                <p className="font-bold">{sheet.billingPeriodYear ?? "—"}</p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Effective Working Hours</p>
                <p className="font-bold">{sheet.billingHours ?? "—"} hrs</p>
              </div>
            </div>
          </div>

          {/* Details section */}
          <div className="border-t mx-5 pt-4 pb-5 space-y-3">
            <h3 className="font-bold text-gray-800">Details</h3>

            {/* Uploaded file */}
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col">
                <p className="text-xs text-gray-500">Uploaded File</p>
                <p className="text-sm font-medium text-gray-700">
                  {sheet.statusReport?.originalFileName ?? "—"}
                </p>
              </div>
              {sheet.statusReport && (
                <button
                  onClick={() => onDownload(sheet.statusReport)}
                  className="text-sm text-blue-500 hover:text-blue-700 underline"
                >
                  Download
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-2">
              <div>
                <p className="text-xs text-gray-500">Submitted By</p>
                <p className="text-sm font-medium text-gray-700">
                  {sheet.submittedByName ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Submitted On</p>
                <p className="text-sm font-medium text-gray-700">{submittedAt}</p>
              </div>

              {sheet.description && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Description</p>
                  <p className="text-sm text-gray-700">{sheet.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: timeline ─────────────────────────────── */}
      <div className="border mt-10 border-gray-300 rounded-2xl bg-white w-1/4 h-full">
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
          className="my-6"
        >
          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot color="primary" />
            </TimelineSeparator>
            <TimelineContent>
              <p className="text-sm text-gray-700">Submitted by</p>
              <p className="text-sm font-bold">{sheet.submittedByName ?? "—"}</p>
              <p className="text-xs text-gray-400">{submittedAt}</p>
            </TimelineContent>
          </TimelineItem>
        </Timeline>
      </div>
    </div>
  );
};

/* ── Main component ────────────────────────────────────────── */
const StatusReportPending = () => {
  const [statusReports, setStatusReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [visibleDetails, setVisibleDetails] = useState({});
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchMyStatusReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${serviceUrl}/api/v2/statusReport/my`, {
        headers: genericHeaders(),
      });
      if (response.status === 200) {
        const raw = response.data?.data ?? response.data;
        const submitted = Array.isArray(raw)
          ? raw.filter((s) => s.status === "SUBMITTED")
          : [];
        setStatusReports(submitted);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch status reports.");
      console.error(err);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    fetchMyStatusReports();
  }, []);

  const toggleDetails = (id) => {
    setVisibleDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleViewDetails = (sheet) => {
    setSelectedSheet(sheet);
    setModalOpen(true);
  };

  const handleDownload = async (doc) => {
    try {
      const s3Url = await get_file_from_s3(doc?.fileName);
      if (!s3Url) {
        toast.error("Failed to fetch the file");
        return;
      }
      const response = await fetch(s3Url);
      const blob = await response.blob();
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.setAttribute("download", doc?.originalFileName || doc?.fileName || "status-report");
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error("Error downloading file:", err);
      toast.error("Failed to download file.");
    }
  };

  if (loading) return <Loader />;

  if (hasFetched && statusReports.length === 0) {
    return (
      <div className="mt-10 flex flex-col items-center justify-center pb-10">
        <img src={NoContent} alt="No Content" />
        <p className="text-xl text-gray-500">No pending status reports found.</p>
      </div>
    );
  }

  return (
    <div className="py-4">
      {/* Shared detail modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent>
          <StatusReportPendingDetailModal
            sheet={selectedSheet}
            onDownload={handleDownload}
          />
        </DialogContent>
        <DialogActions className="my-2 mx-5">
          <Button onClick={() => setModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {statusReports.map((sheet) => {
        const id = sheet.id ?? sheet._id;
        const isOpen = visibleDetails[id] || false;

        const billingPeriod =
          sheet.billingPeriodMonth && sheet.billingPeriodYear
            ? dayjs()
                .month(sheet.billingPeriodMonth - 1)
                .year(sheet.billingPeriodYear)
                .format("MMMM YYYY")
            : "—";

        const submittedAt = sheet.submittedAt
          ? dayjs(sheet.submittedAt + "Z").format("DD MMM YYYY, hh:mm A")
          : "—";

        return (
          <div
            key={id}
            className="w-11/12 my-5 mx-auto border border-slate-300 rounded-xl bg-white"
          >
            {/* ── Collapsed header row ── */}
            <section
              className={`p-3 w-full flex justify-between ${
                isOpen ? "border-b border-slate-300" : ""
              }`}
            >
              <div className="grid grid-cols-4 gap-10 w-11/12">
                <div className="my-auto mx-3">
                  <p className="text-xs text-gray-500">Month</p>
                  <p className="text-base font-medium">{billingPeriod}</p>
                </div>
                <div className="my-auto mx-3">
                  <p className="text-xs text-gray-500">Effective Working Hours</p>
                  <p className="text-base">{sheet.billingHours ?? "—"} hrs</p>
                </div>
                <div className="my-auto mx-3 truncate">
                  <p className="text-xs text-gray-500">File</p>
                  <p className="text-sm truncate">
                    {sheet.statusReport?.originalFileName ?? "—"}
                  </p>
                </div>
                <div className="my-auto mx-3">
                  <p className="text-base font-semibold text-blue-500">
                    {sheet.status}
                  </p>
                </div>
              </div>

              <div className="my-auto w-1/12 flex justify-end pr-3">
                {isOpen ? (
                  <ChevronDoubleUpIcon
                    className="h-6 text-gray-600 cursor-pointer"
                    onClick={() => toggleDetails(id)}
                  />
                ) : (
                  <ChevronDoubleDownIcon
                    className="h-6 text-gray-600 cursor-pointer"
                    onClick={() => toggleDetails(id)}
                  />
                )}
              </div>
            </section>

            {/* ── Expanded section ── */}
            <div
              className={`overflow-hidden transition-all ${
                isOpen ? "max-h-screen" : "max-h-0"
              }`}
            >
              <section className="w-full px-5 py-3 flex flex-col gap-1 border-b border-slate-300">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Submitted on: </span>
                  {submittedAt}
                </p>
              </section>

              <section className="w-full flex justify-end gap-5 px-5 py-3">
                <p
                  className="cursor-pointer text-blue-500 text-base hover:text-blue-700"
                  onClick={() => handleViewDetails(sheet)}
                >
                  View Details
                </p>
              </section>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StatusReportPending;
